import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "antd";
import { IoMdSend } from "react-icons/io";
import { ImAttachment } from "react-icons/im";
import doctorIcon from "../../assets/images/Svg/doctorIcon.svg";
import threeDotIcon from "../../assets/images/Svg/three_dot_icon.svg";
import backArrow from "../../assets/images/Svg/backArrow.svg";
import MessageBubble from "./MessageBubble";
import { buildMessageTimeline, formatDateSeparator } from "./messageTimeline";

function ChatWindow({ doctor, patientId, messages, onBack, onSendMessage }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const formRef = useRef();
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const timeline = useMemo(() => buildMessageTimeline(messages), [messages]);

  const avatarSrc = doctor.imageName
    ? process.env.REACT_APP_IMAGE_URL + doctor.imageName
    : doctorIcon;

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;

    setSending(true);
    try {
      await onSendMessage(trimmed);
      formRef.current?.resetFields();
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-screen border rounded-md">
      <div className="bg-[#FFFFFF] text-[#333333] h-14 px-1 py-1.5 flex justify-between items-center">
        <div className="h-[inherit] flex items-center gap-3">
          <img
            src={backArrow}
            className="font-medium text-xl text-[#333333] mx-2 cursor-pointer"
            alt="Back Arrow"
            onClick={onBack}
          />
          <div className="w-10 h-10 rounded-full flex items-center">
            <img
              src={avatarSrc}
              alt={doctor.name}
              className="w-10 h-10 rounded-full"
            />
          </div>
          <div>
            <p className="font-medium text-base font-roboto">{doctor.name}</p>
          </div>
        </div>
        <img
          src={threeDotIcon}
          className="font-medium text-xl text-[#333333] mx-2 cursor-pointer"
          alt="Three dots"
        />
      </div>

      <div ref={containerRef} className="h-[85%] hideScrollBar px-2 bg-[#F0F2F1]">
        {timeline.map((item) =>
          item.type === "date" ? (
            <div key={item.key} className="text-center text-xs text-gray-500 my-2">
              {formatDateSeparator(item.date)}
            </div>
          ) : (
            <MessageBubble
              key={item.key}
              message={item.message}
              isOwnMessage={item.message.senderID === patientId}
            />
          )
        )}
      </div>

      <Form ref={formRef}>
        <div className="w-full flex items-center gap-2 p-2">
          <div className="w-[90%]">
            <Form.Item name="textMsg" className="m-0 p-0">
              <input
                type="text"
                className="border rounded-3xl outline-none w-full py-2 text-xs px-2 font-medium"
                placeholder="Type message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Form.Item>
          </div>
          <div className="w-[10%]">
            {message.length > 0 ? (
              <div
                onClick={handleSend}
                className="w-7 h-7 rounded-full bg-[#189FAA] flex items-center justify-center cursor-pointer"
              >
                <IoMdSend className="w-5 h-5 text-white" />
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full bg-[#189FAA] flex items-center justify-center cursor-pointer">
                <ImAttachment className="w-5 h-5 text-white rotate-45" />
              </div>
            )}
          </div>
        </div>
      </Form>
    </div>
  );
}

export default ChatWindow;